import React from 'react';
import { GpMeasurement, GpMeasurementCategory } from '../../rtk-apis/gp/gpApi';

/**
 * GP-portal read-only echo measurement worksheet (presentational). Renders the
 * structured measurements fetched by the parent from
 * GET /v2/gp/reports/:id/measurements — headline stat cards (EF / GLS / RVSP /
 * LAVi) plus a worksheet table grouped by category. No editing: the GP sees the
 * finalized clinical numbers only.
 */

const CATEGORY_ORDER: GpMeasurementCategory[] = ['2D', 'MMODE', 'DOPPLER', 'STRAIN', 'DERIVED'];
const CATEGORY_LABELS: Record<GpMeasurementCategory, string> = {
  '2D': '2D',
  MMODE: 'M-Mode',
  DOPPLER: 'Doppler',
  STRAIN: 'Strain',
  DERIVED: 'Derived / Calculated',
};

// Headline parameters surfaced as stat cards (matched case-insensitively by id).
const HEADLINE_IDS = ['EF_BIPLANE', 'EF', 'GLS', 'RVSP', 'LAVI'];
const HEADLINE_SHORT: Record<string, string> = {
  EF_BIPLANE: 'EF',
  EF: 'EF',
  GLS: 'GLS',
  RVSP: 'RVSP',
  LAVI: 'LAVi',
};

const fmt = (v: number | null | undefined): string => {
  if (v == null || isNaN(Number(v))) return '—';
  return String(Math.round(Number(v) * 100) / 100);
};

// Coded/graded parameters (e.g. ASE diastolic-function grade) are stored as a numeric
// code — show the clinical label, not the raw number. Mirror of the catalog valueLabels.
const CODED_LABELS: Record<string, Record<number, string>> = {
  DIAST_GRADE: { 0: 'Normal', 1: 'Grade I', 2: 'Grade II', 3: 'Grade III', 9: 'Indeterminate' },
};
const displayValue = (m: GpMeasurement): string => {
  const map = CODED_LABELS[(m.measurementId || '').toUpperCase()];
  if (map && m.value != null && map[Number(m.value)] != null) {
    return map[Number(m.value)];
  }
  return fmt(m.value);
};

const rangeText = (m: GpMeasurement): string => {
  const nr = m.normalRange;
  if (!nr || nr.low == null || nr.high == null) return '—';
  return `${fmt(nr.low)}–${fmt(nr.high)}${nr.indexed ? ' (idx)' : ''}`;
};

const card = (active: boolean): React.CSSProperties => ({
  flex: '1 1 150px',
  minWidth: '140px',
  background: active ? 'rgba(248,113,113,0.08)' : 'rgba(19,27,47,0.72)',
  border: `1px solid ${active ? 'rgba(248,113,113,0.4)' : 'rgba(255,255,255,0.08)'}`,
  borderRadius: '12px',
  padding: '14px 16px',
  boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
});

const flagPill: React.CSSProperties = {
  display: 'inline-block',
  marginLeft: '8px',
  padding: '1px 7px',
  borderRadius: '4px',
  border: '1px solid rgba(248,113,113,0.5)',
  color: '#f87171',
  fontSize: '10px',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  verticalAlign: 'middle',
};

const GpMeasurements: React.FC<{ measurements: GpMeasurement[] }> = ({ measurements }) => {
  if (!measurements.length) {
    return (
      <div
        style={{
          background: 'rgba(19,27,47,0.72)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '12px',
          padding: '60px 20px',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: '14px', fontWeight: 500, color: 'rgba(255,255,255,0.55)', marginBottom: '4px' }}>
          No measurements recorded
        </div>
        <div style={{ fontSize: '12.5px', color: 'rgba(255,255,255,0.3)' }}>
          This report has no structured echo measurements
        </div>
      </div>
    );
  }

  // Headline stat cards
  const headline = HEADLINE_IDS.map((id) =>
    measurements.find((m) => m.measurementId.toUpperCase() === id)
  ).filter((m): m is GpMeasurement => {
    // de-dupe EF (EF_Biplane vs EF) — keep first match per short label
    return !!m;
  });
  const seenShort = new Set<string>();
  const headlineUnique = headline.filter((m) => {
    const short = HEADLINE_SHORT[m.measurementId.toUpperCase()] || m.measurementId;
    if (seenShort.has(short)) return false;
    seenShort.add(short);
    return true;
  });

  // Group by category
  const byCat: Record<string, GpMeasurement[]> = {};
  measurements.forEach((m) => {
    const cat = CATEGORY_ORDER.includes(m.category) ? m.category : 'DERIVED';
    (byCat[cat] = byCat[cat] || []).push(m);
  });

  const th: React.CSSProperties = {
    textAlign: 'left',
    padding: '9px 14px',
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: 'rgba(255,255,255,0.45)',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
  };
  const td: React.CSSProperties = {
    padding: '9px 14px',
    fontSize: '13.5px',
    color: 'rgba(255,255,255,0.85)',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    verticalAlign: 'top',
  };

  return (
    <div>
      {/* Headline stat cards */}
      {headlineUnique.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '18px' }}>
          {headlineUnique.map((m) => {
            const short = HEADLINE_SHORT[m.measurementId.toUpperCase()] || m.parameterName;
            return (
              <div key={m.measurementId} style={card(m.isAbnormal)}>
                <div
                  style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: m.isAbnormal ? 'rgba(248,113,113,0.85)' : 'rgba(255,255,255,0.45)',
                    marginBottom: '6px',
                  }}
                >
                  {short}
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '5px' }}>
                  <span
                    style={{
                      fontSize: '26px',
                      fontWeight: 700,
                      color: m.isAbnormal ? '#f87171' : '#fff',
                      lineHeight: 1,
                    }}
                  >
                    {displayValue(m)}
                  </span>
                  <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>
                    {m.unit || ''}
                  </span>
                </div>
                <div style={{ fontSize: '11.5px', color: 'rgba(255,255,255,0.35)', marginTop: '6px' }}>
                  Normal {rangeText(m)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Worksheet table */}
      <div
        style={{
          background: 'rgba(19,27,47,0.72)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Parameter</th>
              <th style={{ ...th, textAlign: 'center' }}>Value</th>
              <th style={th}>Unit</th>
              <th style={th}>Normal Range</th>
              <th style={th}>Method / View</th>
            </tr>
          </thead>
          <tbody>
            {CATEGORY_ORDER.filter((c) => byCat[c]?.length).map((cat) => (
              <React.Fragment key={cat}>
                <tr>
                  <td
                    colSpan={5}
                    style={{
                      padding: '7px 14px',
                      fontSize: '11px',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      color: '#4fc3f7',
                      background: 'rgba(30,169,234,0.08)',
                      borderBottom: '1px solid rgba(30,169,234,0.15)',
                    }}
                  >
                    {CATEGORY_LABELS[cat]}
                  </td>
                </tr>
                {byCat[cat].map((m, i) => (
                  <tr
                    key={`${m.measurementId}-${i}`}
                    style={m.isAbnormal ? { background: 'rgba(248,113,113,0.06)' } : undefined}
                  >
                    <td style={td}>{m.parameterName || m.measurementId}</td>
                    <td
                      style={{
                        ...td,
                        textAlign: 'center',
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        color: m.isAbnormal ? '#f87171' : 'rgba(255,255,255,0.95)',
                      }}
                    >
                      {displayValue(m)}
                      {m.isAbnormal && <span style={flagPill}>Abnormal</span>}
                    </td>
                    <td style={td}>{m.unit || '—'}</td>
                    <td style={td}>{rangeText(m)}</td>
                    <td style={td}>{[m.method, m.planeView].filter(Boolean).join(', ') || '—'}</td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <p style={{ fontSize: '11.5px', color: 'rgba(255,255,255,0.3)', marginTop: '12px' }}>
        Read-only worksheet · values as finalized by the reporting clinician
      </p>
    </div>
  );
};

export default GpMeasurements;
